import * as Sentry from "@sentry/react";
import queryString from "query-string";
import { cp } from "../config";
import type { SsoProvider } from "../constants/SsoConstants";
import { getRequest } from "../helpers/networkHelper";
import { getUrlParameter } from "../helpers/Url";
import { getAppBaseUrl } from "../utils/urlUtils";

/**
 * Envelope of `/user/register/`. The endpoint always answers HTTP 200 and reports the real outcome in the
 * body, so `success` (not the HTTP status) decides whether the registration worked.
 */
interface RegisterUserResponse {
    success: boolean;
    sessionId?: string;
    loginLink?: string;
    qrCode?: string;
    error?: string;
    errorCode?: number;
    errorSubCode?: number;
    httpCode?: number;
}

export enum RegistrationOutcomes {
    SUCCESS = "SUCCESS",
    EMAIL_ALREADY_EXISTS = "EMAIL_ALREADY_EXISTS",
    FAILED = "FAILED",
    /** The account exists, but the teacher could not be logged in with it; the emailed password still works. */
    SESSION_FAILED = "SESSION_FAILED",
}

export type RegisterUserResult =
    /** `sessionId` is a ready-to-use JOSSO session for the new account. */
    | { outcome: RegistrationOutcomes.SUCCESS; sessionId: string }
    | { outcome: Exclude<RegistrationOutcomes, RegistrationOutcomes.SUCCESS> };

// The subcode discriminates, not errorCode: that one is 3 for an existing user *and* for a missing email.
const EMAIL_ALREADY_EXISTS_SUB_CODE = 200005;

export const TEACHER_CONTACT_GROUP = "teacher";
const DEFAULT_SOURCE = "p6u-teacher";
// Replaces the chain's default "modular onboarding" in the Datenherkunft field.
const SSO_PROCESS = "teacher assistant";

// Campaign landings (Cornelsen, Didacta, ...) bring their own utm_source, but it is usually gone from the
// URL by the summary page, so the default carries most registrations.
const resolveSource = (): string => getUrlParameter("utm_source") || DEFAULT_SOURCE;

/**
 * Registers a teacher. The account is usable straight away (phase-6 mails a generated password) despite
 * the API docs calling this double opt-in. Reports failures as an outcome rather than throwing, so callers
 * can tell a rejected registration apart from an earlier step failing.
 */
export const registerUser = async (email: string): Promise<RegisterUserResult> => {
    let data: RegisterUserResponse;

    try {
        const response = await getRequest("user/register/", {
            Email: email,
            group: TEACHER_CONTACT_GROUP,
            source: resolveSource(),
        });
        data = response.data;
    } catch (error) {
        console.warn("Registration request failed", error);
        return { outcome: RegistrationOutcomes.FAILED };
    }

    if (data.success) {
        // The account exists at this point, but without a session the caller can neither log the teacher in
        // nor save their test.
        if (!data.sessionId) {
            Sentry.captureMessage("Registration succeeded without a session id");
            return { outcome: RegistrationOutcomes.SESSION_FAILED };
        }
        return { outcome: RegistrationOutcomes.SUCCESS, sessionId: data.sessionId };
    }

    if (data.errorSubCode === EMAIL_ALREADY_EXISTS_SUB_CODE) {
        return { outcome: RegistrationOutcomes.EMAIL_ALREADY_EXISTS };
    }

    // Codes logged here: the only way to tell the remaining failure modes apart.
    console.warn("Registration rejected", data.errorCode, data.errorSubCode, data.error);
    return { outcome: RegistrationOutcomes.FAILED };
};

/**
 * Entry point of the website's Google/Apple chain: Keycloak signs the teacher in with the provider, creates the
 * account if needed, and OpenCms returns to `redirectUrl` unchanged (or with `error` / `error_description`
 * appended). fobizz goes through the website's partner login instead, which takes no `source` / `process` and
 * returns to `onErrorRedirectUrl` on a cancel; the `error` in it sends /auth down its failure path.
 * The `sso` marker is ours, it tells /auth that this login came back from a provider.
 */
export const buildSsoRegisterUrl = (provider: SsoProvider): string => {
    const returnParams = { redirectTo: "/assistent", sso: provider };
    const returnUrl = `${getAppBaseUrl()}/auth?${queryString.stringify(returnParams)}`;

    if (provider === "fobizz") {
        const errorReturnParams = queryString.stringify({ ...returnParams, error: "cancelled" });
        const partnerParams = queryString.stringify({
            partner: provider,
            redirectUrl: returnUrl,
            onErrorRedirectUrl: `${getAppBaseUrl()}/auth?${errorReturnParams}`,
        });
        return `${cp.cfg.REACT_APP_P6_BASE_WEBSITE}partner/login/?${partnerParams}`;
    }

    const params = queryString.stringify({
        provider,
        redirectUrl: returnUrl,
        source: resolveSource(),
        process: SSO_PROCESS,
    });
    return `${cp.cfg.REACT_APP_P6_BASE_WEBSITE}classic/register/oauth-register.jsp?${params}`;
};

/**
 * Gives the logged-in user (identified by the JOSSO cookie) a contact group; the SSO chain sets none. Like
 * `/user/register/`, the endpoint reports failures in the body rather than the HTTP status, so those reject too.
 */
export const setUserGroup = async (group: typeof TEACHER_CONTACT_GROUP): Promise<void> => {
    const { data } = await getRequest("user/group/", { group });
    if (!data?.success) {
        throw new Error(`Setting the user group failed: ${data?.httpCode} ${data?.errorCode} ${data?.error}`);
    }
};
